export type NotificationType =
  | 'general'
  | 'workflow'
  | 'billing'
  | 'contract'
  | 'customer'
  | 'nfse'
  | 'system'
  | 'alert'
  | 'accounts_payable'

export interface Notification {
  uuid: string
  title: string
  body: string | null
  notification_type: NotificationType
  read: boolean
  read_at: string | null
  metadata: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

export interface NotificationListItem {
  uuid: string
  title: string
  body: string | null
  notification_type: NotificationType
  read: boolean
  icon?: string
  navigation_url?: string | null
  event_type?: string
  created_at: string
  // SC-10999: empresa da notificação, só na visão multi-empresas
  merchant?: { uuid?: string; name: string; logo_url?: string | null } | null
}

export interface NotificationsListResponse {
  notifications: NotificationListItem[]
  meta: {
    current_page: number
    next_page: number | null
    prev_page: number | null
    total_pages: number
    total_count: number
    unread_count: number
  }
}

export interface MarkAllReadResponse {
  records_updated: number
}

// Notification event types
export type NotificationEventType =
  | 'billing_paid'
  | 'billing_expiring'
  | 'contract_signed'
  | 'contract_current'
  | 'contract_expiring'
  | 'customer_created'
  | 'customer_replied'
  | 'nfse_confirmed'
  | 'nfse_failed'
  | 'nfse_failed_address'
  | 'transfer_completed'
  | 'transfer_failed'
  | 'recurrence_ending'
  | 'recurrence_adjustment_pending'
  | 'credit_card_assigned_on_recurrence'
  | 'accounts_payable_due_soon'
  | 'payment_failed'
  | 'report_completed'
  | 'report_failed'
  | 'import_completed'
  | 'batch_billing_completed'
  | 'batch_recurrence_completed'
  | 'issue_date_update_completed'
  | 'product_adjustment_completed'

// Notification preference for a single event
export interface NotificationEventPreference {
  enabled: boolean
  digest?: boolean
  digest_frequency?: number
  reminder_days?: number[]
}

// Full notification preferences
export interface NotificationPreferences {
  billing_paid?: NotificationEventPreference
  billing_expiring?: NotificationEventPreference
  contract_signed?: NotificationEventPreference
  contract_current?: NotificationEventPreference
  contract_expiring?: NotificationEventPreference
  customer_created?: NotificationEventPreference
  customer_replied?: NotificationEventPreference
  nfse_confirmed?: NotificationEventPreference
  nfse_failed?: NotificationEventPreference
  nfse_failed_address?: NotificationEventPreference
  transfer_completed?: NotificationEventPreference
  transfer_failed?: NotificationEventPreference
  recurrence_ending?: NotificationEventPreference
  recurrence_adjustment_pending?: NotificationEventPreference
  credit_card_assigned_on_recurrence?: NotificationEventPreference
  accounts_payable_due_soon?: NotificationEventPreference
  payment_failed?: NotificationEventPreference
  report_completed?: NotificationEventPreference
  report_failed?: NotificationEventPreference
  import_completed?: NotificationEventPreference
  batch_billing_completed?: NotificationEventPreference
  batch_recurrence_completed?: NotificationEventPreference
  issue_date_update_completed?: NotificationEventPreference
  product_adjustment_completed?: NotificationEventPreference
  toast_enabled?: boolean
}

// Event configuration for UI
export interface NotificationEventConfig {
  key: NotificationEventType
  label: string
  description: string
  category:
    | 'billing'
    | 'contract'
    | 'customer'
    | 'nfse'
    | 'system'
    | 'transfer'
    | 'accounts_payable'
  supportsDigest?: boolean
}

// All notification event configs for the settings UI
export const NOTIFICATION_EVENT_CONFIGS: NotificationEventConfig[] = [
  {
    key: 'billing_paid',
    label: 'Cobrança paga',
    description: 'Quando uma cobrança é paga',
    category: 'billing'
  },
  {
    key: 'billing_expiring',
    label: 'Cobrança expirada',
    description: 'Quando uma cobrança vence sem pagamento',
    category: 'billing'
  },
  {
    // SC-10917: fica em `billing` (não em `customer`) porque a resposta do
    // cliente é insumo de cobrança — "sexta feira pago" muda a régua. É lida
    // junto com o que decide a cobrança, não na aba de cadastro.
    key: 'customer_replied',
    label: 'Cliente respondeu',
    description:
      'Quando um cliente responde uma notificação de cobrança no WhatsApp',
    category: 'billing'
  },
  {
    key: 'contract_signed',
    label: 'Contrato assinado',
    description: 'Quando um signatário assina um contrato',
    category: 'contract'
  },
  {
    key: 'contract_current',
    label: 'Contrato vigente',
    description:
      'Quando todos os signatários assinam e o contrato entra em vigor',
    category: 'contract'
  },
  {
    key: 'contract_expiring',
    label: 'Contrato expirando',
    description: 'Quando um contrato está próximo do vencimento',
    category: 'contract',
    supportsDigest: true
  },
  {
    key: 'customer_created',
    label: 'Novo cliente',
    description:
      'Quando um novo cliente é cadastrado (via link, checkout ou importação)',
    category: 'customer'
  },
  {
    key: 'nfse_confirmed',
    label: 'Nota fiscal emitida',
    description: 'Quando uma NFSe é emitida com sucesso',
    category: 'nfse'
  },
  {
    key: 'nfse_failed',
    label: 'Erro na nota fiscal',
    description: 'Quando ocorre erro na emissão de NFSe',
    category: 'nfse'
  },
  {
    key: 'nfse_failed_address',
    label: 'Erro na nota fiscal (endereço)',
    description: 'Quando a NFSe falha por problema no endereço do cliente',
    category: 'nfse'
  },
  {
    key: 'transfer_completed',
    label: 'Transferência realizada',
    description: 'Quando uma transferência é concluída com sucesso',
    category: 'transfer'
  },
  {
    key: 'transfer_failed',
    label: 'Transferência falhou',
    description: 'Quando uma transferência falha',
    category: 'transfer'
  },
  {
    key: 'recurrence_ending',
    label: 'Recorrência acabando',
    description: 'Quando uma recorrência está no último ciclo',
    category: 'billing',
    supportsDigest: true
  },
  {
    key: 'recurrence_adjustment_pending',
    label: 'Reajuste pendente',
    description: 'Quando há reajustes pendentes para o próximo mês',
    category: 'billing',
    supportsDigest: true
  },
  {
    key: 'credit_card_assigned_on_recurrence',
    label: 'Cartão vinculado à recorrência',
    description: 'Quando um cartão de crédito é vinculado a uma recorrência',
    category: 'billing'
  },
  {
    key: 'accounts_payable_due_soon',
    label: 'Conta a pagar vencendo',
    description: 'Quando uma conta a pagar está próxima do vencimento',
    category: 'accounts_payable'
  },
  {
    key: 'payment_failed',
    label: 'Falha no cartão recorrente',
    description: 'Quando a cobrança automática no cartão de crédito falha',
    category: 'billing'
  },
  {
    key: 'report_completed',
    label: 'Relatório pronto',
    description: 'Quando um relatório é gerado com sucesso',
    category: 'system'
  },
  {
    key: 'report_failed',
    label: 'Erro ao gerar relatório',
    description: 'Quando ocorre um erro ao gerar um relatório',
    category: 'system'
  },
  {
    key: 'import_completed',
    label: 'Importação concluída',
    description: 'Quando uma importação é processada',
    category: 'system'
  },
  {
    key: 'batch_billing_completed',
    label: 'Cobranças em lote',
    description: 'Quando a criação de cobranças em lote finaliza',
    category: 'system'
  },
  {
    key: 'batch_recurrence_completed',
    label: 'Recorrências em lote',
    description: 'Quando a criação de recorrências em lote finaliza',
    category: 'system'
  },
  {
    key: 'issue_date_update_completed',
    label: 'Atualização de datas de emissão',
    description:
      'Quando a atualização das datas de emissão das cobranças finaliza',
    category: 'system'
  },
  {
    key: 'product_adjustment_completed',
    label: 'Reajuste de plano',
    description: 'Quando o reajuste de um plano é aplicado nas recorrências',
    category: 'system'
  }
]
